import {
  COL_FINISHED_GOODS_LOTS,
  COL_MATERIAL_LOTS,
  COL_PRODUCTION_REQUEST_LINES,
  COL_SCHEDULE_SLOTS,
  COL_SLOT_MATERIAL_USAGE,
} from '../config/collections'
import { pb } from '../pocketbase'
import type { ScheduleSlotRecord, ScheduleSlotStatus } from '../types/records'
import { onSlotStatusChange } from './inventoryReservation'

export type SlotTransitionOptions = {
  actualQuantity?: number | null
  closedBy?: string
  createdBy?: string
  notes?: string
}

async function productIdForSlot(slot: ScheduleSlotRecord): Promise<string | null> {
  const lineRef = slot.requestLine
  if (!lineRef) return null
  if (typeof lineRef === 'string') {
    try {
      const line = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getOne(lineRef)
      const pid = line.product
      return typeof pid === 'string' ? pid : null
    } catch {
      return null
    }
  }
  return null
}

/** Создание партии ГП при закрытии слота (идемпотентно по scheduleSlot). */
export async function createFinishedGoodsLotOnComplete(
  slot: ScheduleSlotRecord,
  productId: string,
): Promise<void> {
  if (slot.status !== 'completed' || !productId) return
  const qty = slot.actualQuantity != null ? Number(slot.actualQuantity) : 0
  if (qty <= 0) return

  try {
    const existing = await pb.collection(COL_FINISHED_GOODS_LOTS).getList(1, 1, {
      filter: `scheduleSlot="${slot.id}"`,
    })
    if (existing.items.length > 0) return
  } catch {
    /* продолжаем попытку создания */
  }

  const producedAt = (slot.plannedEnd || slot.plannedStart || new Date().toISOString()).slice(0, 10)
  await pb.collection(COL_FINISHED_GOODS_LOTS).create({
    product: productId,
    quantity: qty,
    producedAt,
    scheduleSlot: slot.id,
    lotCode: `FG-${slot.id.slice(0, 8).toUpperCase()}`,
  })
}

/** Привязка партии сырья к слоту при закрытии (прослеживаемость). */
export async function linkMaterialLotToSlot(
  slotId: string,
  lotCode: string,
  quantityUsed?: number,
): Promise<string | null> {
  const code = lotCode.trim()
  if (!code) return null
  try {
    const lot = await pb.collection(COL_MATERIAL_LOTS).getFirstListItem(`lotCode="${code}"`)
    const qty = quantityUsed != null && quantityUsed > 0 ? quantityUsed : 1
    await pb.collection(COL_SLOT_MATERIAL_USAGE).create({
      slot: slotId,
      materialLot: lot.id,
      quantityUsed: qty,
    })
    return null
  } catch {
    return `Партия сырья «${code}» не найдена`
  }
}

/** Смена статуса слота с резервом/списанием сырья через inventoryReservation. */
export async function transitionSlotStatus(
  slotId: string,
  nextStatus: ScheduleSlotStatus,
  options: SlotTransitionOptions = {},
): Promise<ScheduleSlotRecord> {
  const slot = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
  const prevStatus = slot.status

  const payload: Record<string, unknown> = { status: nextStatus }
  if (options.notes !== undefined) payload.notes = options.notes
  if (options.actualQuantity !== undefined) payload.actualQuantity = options.actualQuantity
  if (nextStatus === 'completed' && options.closedBy) payload.closedBy = options.closedBy

  const updated = await pb.collection(COL_SCHEDULE_SLOTS).update<ScheduleSlotRecord>(slotId, payload)

  const productId = await productIdForSlot(slot)
  if (productId) {
    await onSlotStatusChange(
      { ...updated, id: slotId },
      productId,
      prevStatus,
      nextStatus,
      options.createdBy,
    )
    if (prevStatus !== 'completed' && nextStatus === 'completed') {
      await createFinishedGoodsLotOnComplete({ ...updated, id: slotId }, productId)
    }
  }

  return updated
}

/** После create/update слота — обработка смены статуса (резерв / списание). */
export async function afterSlotSaved(
  slot: ScheduleSlotRecord,
  prevStatus: ScheduleSlotStatus | null,
  productId: string | null,
  createdBy?: string,
): Promise<void> {
  if (!productId || prevStatus == null || prevStatus === slot.status) return
  await onSlotStatusChange(slot, productId, prevStatus, slot.status, createdBy)
  if (prevStatus !== 'completed' && slot.status === 'completed') {
    await createFinishedGoodsLotOnComplete(slot, productId)
  }
}
